import '../styles/pdf-pages.css';
import '../styles/columnar-table.css';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateFestivalCo2 } from '@/domain/impact-calculate-festival/entities/calculate-festival-co2.entity';
import { CalculateFestivalEnv } from '@/domain/impact-calculate-festival/entities/calculate-festival-env.entity';
import { CalculateFestivalEss } from '@/domain/impact-calculate-festival/entities/calculate-festival-ess.entity';
import {
  selectImpactCalculateFestivalCo2ForProject,
  selectImpactCalculateFestivalEnvForProject,
  selectImpactCalculateFestivalEssForProject,
} from '@/domain/impact-calculate-festival/impact-calculate-festival.selectors';
import { selectCurrentProjectFestival } from '@/domain/project-festival/project-festival.selectors';
import { GaugeScore } from '@/ui/components/score-bars/GaugeScore';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { getColorForScore } from '@/ui/utils/get-color-for-score';

import { formatProjectDatesTz } from '../../../utils/format-project-dates';
import CalendarMonthSvg from '../assets/calendar_month.svg';
import FairlyBackgroundLogo from '../assets/logo-fairly-clear-background.png';
import { PdfProjectPager } from '../components/PdfProjectPager';
import { PdfScoreCard } from '../components/PdfScoreCard';
import {
  displayDecimal,
  displayTonsToKg,
  formatTopicScore,
} from '../utils/display-scores.utils';

export function PdfFestivalPage1({ totalPages }: { totalPages: number }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const festival = useAppSelector(selectCurrentProjectFestival);
  const festivalId = festival?.id ?? 0;
  const impactCo2 = useAppSelector(
    selectImpactCalculateFestivalCo2ForProject(festivalId),
  ) satisfies CalculateFestivalCo2;

  const impactEnv = useAppSelector(
    selectImpactCalculateFestivalEnvForProject(festivalId),
  ) satisfies CalculateFestivalEnv;

  const impactEss = useAppSelector(
    selectImpactCalculateFestivalEssForProject(festivalId),
  ) satisfies CalculateFestivalEss;

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!festival) {
    return <h1>{translate('PdfExports.festival-not-found')}</h1>;
  }

  const impactEnvScore100 =
    !impactEnv.score || !impactEnv.maxScore
      ? 0
      : Math.round((impactEnv.score * 100) / impactEnv.maxScore);

  const impactEssScore100 =
    !impactEss.score || !impactEss.maxScore
      ? 0
      : Math.round((impactEss.score * 100) / impactEss.maxScore);

  /**
   * The title depends on the festival name, it is critical to display it
   * on one line, while keeping it readable.
   */
  const computedFontSize =
    festival.name.length <= 22
      ? 40
      : Math.max(20, 40 - (festival.name.length - 22) * (20 / 28));

  return (
    <div id="page1" className="pdf-page">
      <div className="content">
        <div className="pdf-header">
          <img
            className="festival-logo"
            src={festival.image || FairlyBackgroundLogo}
            alt={translate('PdfExports.festival-logo')}
          />
          <div>
            <h1 style={{ fontSize: computedFontSize }}>{festival.name}</h1>
            {festival.startDate && festival.endDate && (
              <div className="header-subtitles">
                <img
                  src={CalendarMonthSvg}
                  width="19"
                  height="19"
                  style={{ position: 'relative', top: 4, marginRight: 5 }}
                  alt="Calendar icon"
                />
                <span className="festival-date">
                  {formatProjectDatesTz(
                    festival.startDate || '',
                    festival.endDate || '',
                  )}
                </span>
              </div>
            )}
            <div className="pills">
              <span className="tag-pill pill-list">
                {translate('PdfExports.event')}
              </span>
              <span className="tag-pill pill-list">
                {translate('PdfExports.past')}
              </span>
            </div>
          </div>
        </div>

        <div className="pdf-separator"></div>

        <div className="columnar-table">
          <h2>{translate('PdfExports.columnar-table')}</h2>
          <div className="columnar-table-scores">
            <div className="score-item" id="overview-co2">
              <div className="score-item-header">
                <h3>
                  <PdfScoreCard
                    type="co2"
                    scoreLetter={impactCo2.scoreLetter}
                  />
                  {translate('Projects.co2')}
                </h3>
              </div>
              <div className="score-item-value">
                <div
                  style={{
                    position: 'relative',
                    top: 10,
                    marginTop: 20,
                    marginBottom: 20,
                  }}
                >
                  <GaugeScore
                    score={impactEnv.topics.GLOBAL_CO2.score}
                    maxScore={impactEnv.topics.GLOBAL_CO2.maxScore}
                    textColor="black"
                    bgColor="#DDDDDD99"
                    percentageScale={impactEnv.topics.GLOBAL_CO2.maxScore}
                    overrideColor={getColorForScore(impactCo2.scoreLetter).main}
                  />
                </div>
                <ul>
                  <li className="score-highlight">
                    <span>{translate('PdfExports.total-emissions')}</span>
                    <span>
                      <span className="highlight">
                        {displayTonsToKg(impactCo2.scoreKgeCO2)}
                      </span>{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li className="score-highlight-secondary">
                    <span>
                      {translate('Projects.emissions-by-participant')}
                    </span>
                    <span>
                      {displayDecimal(impactCo2.kgeCO2ByParticipant)}{' '}
                      {translate('Projects.kgeCO2')}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Projects.team')}</span>
                    <span>
                      {displayDecimal(impactCo2.sectors.team.score)}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Projects.artists')}</span>
                    <span>
                      {displayDecimal(impactCo2.sectors.artists.score)}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Projects.public')}</span>
                    <span>
                      {displayDecimal(impactCo2.sectors.public.score)}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Projects.site')}</span>
                    <span>
                      {displayDecimal(impactCo2.sectors.site.score)}{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="score-item" id="overview-env">
              <div className="score-item-header">
                <h3>
                  <PdfScoreCard
                    type="env"
                    scoreLetter={impactEnv.scoreLetter}
                  />
                  {translate('Projects.env')}
                </h3>
              </div>
              <div className="score-item-value">
                <div
                  style={{
                    position: 'relative',
                    top: 10,
                    marginTop: 20,
                    marginBottom: 20,
                  }}
                >
                  <GaugeScore
                    textColor="black"
                    score={impactEnvScore100}
                    maxScore={100}
                    bgColor="#DDDDDD99"
                    overrideColor={getColorForScore(impactEnv.scoreLetter).main}
                  />
                </div>
                <ul>
                  <li className="score-highlight">
                    <span>{translate('PdfExports.total-score')}</span>
                    <span>
                      <span className="highlight">
                        {displayDecimal(impactEnv.score)}
                      </span>
                      /{impactEnv.maxScore}
                    </span>
                  </li>
                  <li className="score-highlight-secondary">
                    <span>{translate('Env.Topics.GLOBAL_CO2')}</span>
                    <span>{formatTopicScore(impactEnv.topics.GLOBAL_CO2)}</span>
                  </li>
                  <li>
                    <span>{translate('Env.Topics.FOOD_BEVERAGES')}</span>
                    <span>
                      {formatTopicScore(impactEnv.topics.FOOD_BEVERAGES)}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Env.Topics.RESOURCES_POLLUTION')}</span>
                    <span>
                      {formatTopicScore(impactEnv.topics.RESOURCES_POLLUTION)}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Env.Topics.COMMUNICATION')}</span>
                    <span>
                      {formatTopicScore(impactEnv.topics.COMMUNICATION)}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Env.Topics.PUBLIC_ENGAGEMENT')}</span>
                    <span>
                      {formatTopicScore(impactEnv.topics.PUBLIC_ENGAGEMENT)}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="score-item" id="overview-ess">
              <div className="score-item-header">
                <h3>
                  <PdfScoreCard
                    type="ess"
                    scoreLetter={impactEss.scoreLetter}
                  />
                  {translate('Projects.ess')}
                </h3>
              </div>
              <div className="score-item-value">
                <div
                  style={{
                    position: 'relative',
                    top: 10,
                    marginTop: 20,
                    marginBottom: 20,
                  }}
                >
                  <GaugeScore
                    score={impactEssScore100}
                    maxScore={100}
                    textColor="black"
                    bgColor="#DDDDDD99"
                    overrideColor={getColorForScore(impactEss.scoreLetter).main}
                  />
                </div>
                <ul>
                  <li className="score-highlight">
                    <span>{translate('PdfExports.total-score')}</span>
                    <span>
                      <span className="highlight">
                        {displayDecimal(impactEss.score)}
                      </span>
                      /{impactEss.maxScore}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Ess.Topics.PARITY_EQUALITY')}</span>
                    <span>
                      {formatTopicScore(impactEss.topics.PARITY_EQUALITY)}
                    </span>
                  </li>
                  <li>
                    <span>
                      {translate('Ess.Topics.ACCESSIBILITY_SOCIAL_DIALOGUE')}
                    </span>
                    <span>
                      {formatTopicScore(
                        impactEss.topics.ACCESSIBILITY_SOCIAL_DIALOGUE,
                      )}
                    </span>
                  </li>
                  <li>
                    <span>
                      {translate('Ess.Topics.LOCAL_IMPACT_RSE_ENGAGEMENT')}
                    </span>
                    <span>
                      {formatTopicScore(
                        impactEss.topics.LOCAL_IMPACT_RSE_ENGAGEMENT,
                      )}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="navigation-controls">
          <a href="#page2" className="tag-pill">
            {translate('PdfExports.go-to-score-details')}
          </a>
        </div>
      </div>

      <PdfProjectPager current={1} total={totalPages}></PdfProjectPager>
    </div>
  );
}
